import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
	DndContext,
	closestCenter,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	SortableContext,
	verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import classNames from 'classnames';
import { IHomepageMainNavItems, NavMainItems } from '../../../../../../../types/working-model';
import { GetDefaultMainNavItems } from '../../../../../../../store/utils/create-event';
import { updateHomepageMainNav } from '../../../../../../../store/actions/admin/create-event';
import { updateTranslateKey } from '../../../../../../../utils/utils';
import { ChecklistTitles } from '../../../../../../../store/reducers/admin/assistant';
import { addChecklistItem } from '../../../../../../../store/actions/authentication';
import SortableSessionNavItem from './sortable-nav-item';
import { useTypedSelector } from '../../../../../../../store/reducers/use-typed-selector';
import './event-main-navigation.scss';
import { useParams } from 'react-router';
import { ParamsProps } from '../../../../../../live-event/live-event';
import { getHomepageDefaultLanguage } from '../../../../../../../utils/get-language';

const MainNavigation: React.FC = () => {

	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const user = useTypedSelector(state => state.AuthReducer.user);
	const token = useTypedSelector(state => state.AuthReducer.token);

	const { language } = useParams() as ParamsProps;
	const dispatch = useDispatch();

	const sensors = useSensors(
		useSensor(PointerSensor),
	);

	const isSingleSession = workingEvent?.sessions?.length === 1;

	const navItems = useMemo(() => (
		workingEvent?.homepage?.event_main_nav_items || GetDefaultMainNavItems(isSingleSession)
	), [workingEvent?.homepage?.event_main_nav_items, isSingleSession]);

	function handleDragEnd(event: any) {
		const _mainNavItems = [...navItems];
		const startIndex = _mainNavItems.findIndex((navItem: IHomepageMainNavItems) => navItem.name === event.active.id);
		const newIndex = _mainNavItems.findIndex((navItem: IHomepageMainNavItems) => navItem.name === event.over.id);

		if (startIndex !== newIndex) {
			const [navItem] = _mainNavItems.splice(startIndex, 1);
			_mainNavItems.splice(newIndex, 0, navItem);
			// update database with updated items
			dispatch(updateHomepageMainNav(_mainNavItems));
		}

	}

	const handleViewerClick = (navItem: IHomepageMainNavItems) => {
		const _mainNavItems = navItems.map(ni => {
			if (ni.name === navItem.name) {
				return {
					...ni,
					is_on: !ni.is_on,
				};
			}
			return ni;
		});
		dispatch(updateHomepageMainNav(_mainNavItems));
	};

	const handleNavItemClick = (clickedNavItem: NavMainItems) => () => {
		// ASSISTANT CHECKLIST ITEM: complete user checklist item for editing sessions
		if (clickedNavItem === NavMainItems.Sessions) {
			if (
				user &&
				!user.onboarded.includes(ChecklistTitles.editSessions)
			) dispatch(addChecklistItem(token!, user.id, user.onboarded, ChecklistTitles.editSessions));
		}
	};

	const handleUpdateTitle = (value: string, navItem: IHomepageMainNavItems) => {
		// update name in redux and db
		const _mainNavItems = navItems.map(ni => {
			if (ni.name === navItem.name) {
				return {
					...ni,
					custom_name: updateTranslateKey({
						translateString: navItem.custom_name,
						input: value,
						language,
						baseLanguage: getHomepageDefaultLanguage()
					}),
				};
			}
			return ni;
		});
		dispatch(updateHomepageMainNav(_mainNavItems));
	};

	return (
		<div className="event-main-navigation-container">
			<div className="main-navigation-title">
				<h6>Main navigation</h6>

				{/* Removing for 3.0 release but will be need post MVP */}
				{/* <div>
					<button
						className="no-style clear"
						onClick={() => ({})}
					>
						<Icon name={ICONS.EDIT} color={COLORS.CYAN} size={12} /> <span>Edit</span>
					</button>
					<button
						className="no-style clear"
						onClick={() => ({})}
					>
						<Icon name={ICONS.ADD} color={COLORS.CYAN} size={12} /> <span>Add page</span>
					</button>
				</div> */}
			</div>
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragEnd={handleDragEnd}
				modifiers={[restrictToParentElement]}
				autoScroll={false}
			>
				<SortableContext
					items={navItems.map((navItem: IHomepageMainNavItems) => navItem.name)}
					strategy={verticalListSortingStrategy}
				>
					<div className={classNames("event-main-nav-items-container", { singleSession: isSingleSession })}>
						{navItems!
							.filter(nav => nav.name !== "People") // Removing til post MVP
							.map((navItem: IHomepageMainNavItems) => (
								<SortableSessionNavItem
									key={navItem.name}
									navItem={navItem}
									id={navItem.name}
									handleNavItemClick={handleNavItemClick}
									handleViewerClick={handleViewerClick}
									handleUpdateTitle={handleUpdateTitle}
									workingEvent={workingEvent!}
								/>
							))}
					</div>
				</SortableContext>
			</DndContext>
		</div>
	);
};

export default MainNavigation;
